import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

const statusCodeOf = (res) => String(res?.statusCode || res?.status_code || '');

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  if (!token) {
    return { status_code: '50000', statusCode: '50000', message: 'Not authenticated' };
  }

  const { headers: optionHeaders, ...rest } = options;

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        ...(optionHeaders || {}),
      },
    });

    const data = await response.json().catch(() => ({}));
    const code = data?.status_code ?? data?.statusCode;
    if (!response.ok && (data == null || code == null)) {
      return {
        status_code: '50000',
        statusCode: '50000',
        message: data?.message || `Request failed (${response.status})`,
        httpStatus: response.status,
      };
    }

    return {
      ...data,
      statusCode: data.statusCode ?? data.status_code,
      status_code: data.status_code ?? data.statusCode,
      httpStatus: response.status,
    };
  } catch {
    return { status_code: '50000', statusCode: '50000', message: 'Request failed' };
  }
};

export const isPitchWordApiSuccess = (res) => statusCodeOf(res) === '10000';

export const getPitchWordPayload = (res) => res?.data ?? null;

/** Delete is 409 with this code when attempts exist and `force` was not sent. */
export const isPlayersExist = (res) => {
  if (res?.data?.code === 'PLAYERS_EXIST') return true;
  return String(res?.message || '') === 'PLAYERS_EXIST';
};

/** True when save or regenerate was rejected until `force: true` is sent. */
export const needsForce = (res) => {
  if (isPlayersExist(res)) return true;
  if (res?.data?.requires_force === true || res?.requires_force === true) return true;
  const msg = String(res?.message || '').toLowerCase();
  if (msg.includes('force')) return true;
  if (!msg.includes('already')) return false;
  return /player|opened|open|progress|started|attempt|have|published/.test(msg);
};

export const isPitchWordNotFound = (res) =>
  res?.httpStatus === 404 || /not found/i.test(String(res?.message || ''));

/** GET /admin/arcade/pitch-word?limit=&offset= */
export const listPitchWordPuzzles = ({ limit = 20, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return authFetch(`/admin/arcade/pitch-word?${params.toString()}`, { method: 'GET' });
};

/** GET /admin/arcade/pitch-word/days/:dayKey */
export const getPitchWordDay = (dayKey) =>
  authFetch(`/admin/arcade/pitch-word/days/${encodeURIComponent(dayKey)}`, { method: 'GET' });

/** PUT /admin/arcade/pitch-word/days/:dayKey */
export const savePitchWordDay = (dayKey, body) =>
  authFetch(`/admin/arcade/pitch-word/days/${encodeURIComponent(dayKey)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** POST /admin/arcade/pitch-word/days/:dayKey/regenerate */
export const regeneratePitchWordDay = (dayKey, { force = false } = {}) =>
  authFetch(`/admin/arcade/pitch-word/days/${encodeURIComponent(dayKey)}/regenerate`, {
    method: 'POST',
    body: JSON.stringify({ force: !!force }),
  });

/** DELETE /admin/arcade/pitch-word/days/:dayKey — a deleted date stays empty until save or regenerate. */
export const deletePitchWordDay = (dayKey, { force = false } = {}) =>
  authFetch(`/admin/arcade/pitch-word/days/${encodeURIComponent(dayKey)}`, {
    method: 'DELETE',
    body: JSON.stringify(force ? { force: true } : {}),
  });
